import { loadData, UserDataProvider } from "../../../Core/UserDataProvider"
import { EmailsQueryParams } from "../../../DataTypes/mail"
import { EmailTemplatesQueryParams } from "../../../DataTypes/templates"
import { controllers } from "../../Controllers"


class MailController {

    // ── Settings ──
    getSettings = async () => {
        const { userToken } = controllers().auth
        if(!userToken) return
        const response = loadData(UserDataProvider.getEmailSettings, {userToken})
        return response
    }

    updateSettings = async (fields: any) => {
        const { userToken } = controllers().auth
        if(!userToken) return
        const response = loadData(UserDataProvider.updateEmailSettings, {userToken, ...fields})
        return response
    }

    testConnection = async () => {
        const { userToken } = controllers().auth
        if(!userToken) return
        const response = loadData(UserDataProvider.testEmailConnection, {userToken})
        return response
    }

    // ── Reusable send templates ──
    getTemplates = async (params: EmailTemplatesQueryParams = {}) => {
        const { userToken } = controllers().auth
        if(!userToken) return
        const response = loadData(UserDataProvider.getEmailTemplates, {userToken, ...params})
        return response
    }

    addTemplate = async (fields: any) => {
        const { userToken } = controllers().auth
        if(!userToken) return
        const response = loadData(UserDataProvider.addEmailTemplate, {userToken, ...fields})
        return response
    }

    editTemplate = async (templateId: number, fields: any) => {
        const { userToken } = controllers().auth
        if(!userToken) return
        const response = loadData((body:any) => UserDataProvider.editEmailTemplate(templateId, body), {userToken, ...fields})
        return response
    }

    deleteTemplate = async (templateId: number) => {
        const { userToken } = controllers().auth
        if(!userToken) return
        const response = loadData((body:any) => UserDataProvider.deleteEmailTemplate(templateId, body), {userToken})
        return response
    }

    // Renders the template as-saved with a real lead's data (leadId) or
    // sample data otherwise. Operates on the saved row, not arbitrary
    // unsaved content, so callers should persist first if previewing edits.
    previewTemplate = async (templateId: number, leadId?: number, forCampaign?: boolean) => {
        const { userToken } = controllers().auth
        if(!userToken) return
        const body: any = { userToken }
        if (leadId) body.lead_id = leadId
        if (forCampaign) body.for_campaign = true
        const response = loadData((b:any) => UserDataProvider.previewEmailTemplate(templateId, b), body)
        return response
    }

    // ── Shared signature template ──
    getSharedSignature = async () => {
        const { userToken } = controllers().auth
        if(!userToken) return
        const response = loadData(() => UserDataProvider.getSharedSignature(userToken), {})
        return response
    }

    updateSharedSignature = async (html: string) => {
        const { userToken } = controllers().auth
        if(!userToken) return
        const response = loadData(UserDataProvider.updateSharedSignature, {userToken, html})
        return response
    }

    // ── Inbox ──
    syncInbox = async () => {
        const { userToken } = controllers().auth
        if(!userToken) return
        const response = loadData(UserDataProvider.syncInbox, {userToken})
        return response
    }

    sendEmail = async (fields: any) => {
        const { userToken } = controllers().auth
        if(!userToken) return
        const response = loadData(UserDataProvider.sendEmail, {userToken, ...fields})
        return response
    }

    // Queues an async bulk send (Celery task on the backend) rather than
    // sending inline — the response only confirms the campaign was queued,
    // not that any individual email went out.
    triggerCampaignSend = async (templateId: number, leadIds: number[]) => {
        const { userToken } = controllers().auth
        if(!userToken) return
        const response = loadData(UserDataProvider.triggerCampaignSend, {userToken, template_id: templateId, lead_ids: leadIds})
        return response
    }

    listEmails = async (params: EmailsQueryParams = {}) => {
        const { userToken } = controllers().auth
        if(!userToken) return
        const response = loadData(UserDataProvider.listEmails, {userToken, ...params})
        return response
    }

    getEmailDetail = async (emailId: number) => {
        const { userToken } = controllers().auth
        if(!userToken) return
        const response = loadData((body:any) => UserDataProvider.getEmailDetail(emailId, body), {userToken})
        return response
    }

    getEmailThread = async (emailId: number) => {
        const { userToken } = controllers().auth
        if(!userToken) return
        const response = loadData((body:any) => UserDataProvider.getEmailThread(emailId, body), {userToken})
        return response
    }

    getLeadEmailTimeline = async (leadId: number) => {
        const { userToken } = controllers().auth
        if(!userToken) return
        const response = loadData((body:any) => UserDataProvider.getLeadEmailTimeline(leadId, body), {userToken})
        return response
    }

    markRead = async (emailId: number) => {
        const { userToken } = controllers().auth
        if(!userToken) return
        const response = loadData((body:any) => UserDataProvider.markEmailRead(emailId, body), {userToken})
        return response
    }

    markUnread = async (emailId: number) => {
        const { userToken } = controllers().auth
        if(!userToken) return
        const response = loadData((body:any) => UserDataProvider.markEmailUnread(emailId, body), {userToken})
        return response
    }

    deleteEmail = async (emailId: number) => {
        const { userToken } = controllers().auth
        if(!userToken) return
        const response = loadData((body:any) => UserDataProvider.deleteEmail(emailId, body), {userToken})
        return response
    }
}

export { MailController }
